import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, balanceLabel, dateFmt, dateTimeFmt, money, PAYMENT_RU, phoneFmt, todayIso } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, Status, Tile, useToast } from '../ui';

type Card = {
  client: {
    id: string; code: string; name: string; phone: string; type: string; comment: string | null;
    cachedBalanceKop: string; cachedBottles: number; creditLimitKop: string; creditBlocked: boolean;
    telegramId: string | null; maxId: string | null; priceWater19Kop: string | null;
    addresses: Array<{ id: string; street: string; house: string; flat: string | null; comment: string | null }>;
    subscriptions: Array<{ id: string; active: boolean; weekdays: number[]; everyNWeeks: number; items: Array<{ qty: number; product: { name: string } }> }>;
    orders: Array<{ id: string; number: string; status: string; deliveryDate: string; totalKop: string; paymentMethod: string; delivery: { givenFull: number; takenEmpty: number } | null }>;
  };
  balance: { cachedKop: string; ledgerKop: string; cachedBottles: number; ledgerBottles: number; inSync: boolean };
  credit: { allowed: boolean; reason: string | null; limitKop: string; availableKop: string };
};

type Ledger = {
  money: Array<{ id: string; kind: string; amountKop: string; comment: string | null; createdAt: string; order: { number: string } | null; author: { name: string } | null }>;
  bottles: Array<{ id: string; kind: string; qty: number; comment: string | null; createdAt: string; order: { number: string } | null }>;
};

const WEEKDAYS = ['', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const MONEY_KIND_RU: Record<string, string> = {
  CHARGE: 'Начисление',
  PAYMENT: 'Оплата',
  REFUND: 'Возврат',
  DEPOSIT: 'Залог за тару',
  DEPOSIT_RETURN: 'Возврат залога',
  ADJUSTMENT: 'Корректировка',
  REVERSAL: 'Сторно',
};
const BOTTLE_KIND_RU: Record<string, string> = {
  ISSUE: 'Выдана полная',
  RETURN: 'Забрана пустая',
  LOSS: 'Списание',
  ADJUSTMENT: 'Корректировка',
  REVERSAL: 'Сторно',
};

export function ClientCard() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<Card>(id ? `/clients/${id}` : null);
  const ledger = useApi<Ledger>(id ? `/clients/${id}/ledger?limit=60` : null);
  const [tab, setTab] = useState<'orders' | 'money' | 'bottles'>('orders');
  const [pane, setPane] = useState<'none' | 'edit' | 'address'>('none');
  const toast = useToast();
  const nav = useNavigate();

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const c = data.client;
  const b = balanceLabel(c.cachedBalanceKop);

  const takePayment = async () => {
    const rub = prompt('Сумма оплаты, ₽');
    if (!rub) return;
    const kop = Math.round(Number(rub.replace(',', '.')) * 100);
    if (!Number.isFinite(kop) || kop <= 0) return alert('Не похоже на сумму');
    await api(`/clients/${c.id}/payments`, { body: { amountKop: String(kop), method: 'CASH' } });
    toast.show('Оплата принята');
    await reload();
    await ledger.reload();
  };

  /**
   * Корректировка — не «поправить баланс», а отдельная проводка с причиной.
   * Остаток из неё пересчитается сам: он всегда сумма журнала (инвариант 2).
   */
  const adjust = async () => {
    const rub = prompt('Корректировка денег, ₽ (минус — списать, пусто — только тара)') ?? '';
    const bottles = prompt('Корректировка тары, шт. (минус — снять с клиента)') ?? '';
    const comment = prompt('Причина корректировки (обязательно, останется в истории)');
    if (!comment) return;
    const body: Record<string, unknown> = { comment };
    if (rub.trim()) body.amountKop = String(Math.round(Number(rub.replace(',', '.')) * 100));
    if (bottles.trim()) body.bottles = Number(bottles);
    try {
      await api(`/clients/${c.id}/adjust`, { body });
      toast.show('Корректировка проведена');
      await reload();
      await ledger.reload();
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Не получилось', 5000);
    }
  };

  /** Клиент не удаляется физически — за ним история (инвариант 8). */
  const archive = async () => {
    if (!confirm(`Скрыть клиента «${c.name}»? Он пропадёт из рабочих списков, история останется.`)) return;
    try {
      await api(`/clients/${c.id}`, { method: 'DELETE' });
      toast.show('Клиент скрыт');
      nav('/clients');
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Не получилось', 6000);
    }
  };

  const reverse = async (kind: 'money' | 'bottles', entryId: string) => {
    const comment = prompt('Причина сторно (она останется в истории)');
    if (!comment) return;
    await api(`/ledger/${kind}/${entryId}/reverse`, { body: { comment } });
    toast.show('Сторно проведено');
    await reload();
    await ledger.reload();
  };

  return (
    <>
      <div className="spread">
        <h1>
          {c.name} <span className="muted mono" style={{ fontSize: 14 }}>{c.code}</span>
        </h1>
        <div className="row">
          <button className="primary" onClick={takePayment}>
            Принять оплату
          </button>
          <button onClick={() => setPane(pane === 'edit' ? 'none' : 'edit')}>Изменить</button>
          <button className="ghost" onClick={() => void adjust()}>
            Корректировка
          </button>
          <button className="ghost" onClick={() => void archive()}>
            Скрыть
          </button>
        </div>
      </div>

      {pane === 'edit' ? (
        <EditClient
          client={c}
          onClose={() => setPane('none')}
          onSaved={async () => {
            setPane('none');
            toast.show('Клиент сохранён');
            await reload();
          }}
        />
      ) : null}

      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Tile label="Баланс" value={b.text} tone={b.kind === 'debt' ? 'debt' : b.kind === 'prepaid' ? 'prepaid' : undefined} />
        <Tile label="Тара на руках" value={`${c.cachedBottles} шт.`} />
        <Tile
          label="Отсрочка"
          value={data.credit.allowed ? 'Разрешена' : 'Нет'}
          sub={data.credit.allowed ? `доступно ${money(data.credit.availableKop)}` : data.credit.reason}
        />
        <Tile label="Телефон" value={<span style={{ fontSize: 18 }}>{phoneFmt(c.phone)}</span>} sub={c.type === 'COMPANY' ? 'юрлицо' : 'физлицо'} />
      </div>

      {!data.balance.inSync ? (
        <div className="card" style={{ borderColor: 'var(--amber)' }}>
          <b>Кэш баланса разошёлся с журналом.</b>{' '}
          <span className="muted">
            Кэш {money(data.balance.cachedKop)} / {data.balance.cachedBottles} бут., журнал{' '}
            {money(data.balance.ledgerKop)} / {data.balance.ledgerBottles} бут. Прав журнал.
          </span>{' '}
          <button
            onClick={async () => {
              await api('/admin/recalc', { body: { clientId: c.id } });
              toast.show('Пересчитано из проводок');
              await reload();
            }}
          >
            Пересчитать
          </button>
        </div>
      ) : null}

      <div className="row">
        <div className="card" style={{ flex: '1 1 300px' }}>
          <div className="spread">
            <h2>Адреса</h2>
            <button className="ghost" onClick={() => setPane(pane === 'address' ? 'none' : 'address')}>
              + Адрес
            </button>
          </div>
          {c.addresses.map((a) => (
            <div key={a.id} style={{ marginBottom: 8 }}>
              {a.street}, {a.house}
              {a.flat ? `, ${a.flat}` : ''}
              {a.comment ? <div className="muted">{a.comment}</div> : null}
            </div>
          ))}
          {!c.addresses.length ? <Empty>Адресов нет</Empty> : null}
          {pane === 'address' ? (
            <NewAddress
              clientId={c.id}
              onSaved={async () => {
                setPane('none');
                toast.show('Адрес добавлен');
                await reload();
              }}
            />
          ) : null}
        </div>

        <div className="card" style={{ flex: '1 1 300px' }}>
          <h2>Повторяющиеся доставки</h2>
          {c.subscriptions.length ? (
            c.subscriptions.map((s) => (
              <div key={s.id} style={{ marginBottom: 8 }}>
                <div className="spread">
                  <b>{s.weekdays.map((w) => WEEKDAYS[w]).join(', ')}</b>
                  <button
                    className="ghost"
                    data-sub={s.id}
                    onClick={async () => {
                      await api(`/subscriptions/${s.id}`, { method: 'PATCH', body: { active: !s.active } });
                      toast.show(s.active ? 'Подписка на паузе' : 'Подписка снова работает');
                      await reload();
                    }}
                  >
                    {s.active ? 'Пауза' : 'Возобновить'}
                  </button>
                </div>
                {s.everyNWeeks > 1 ? <span className="muted">раз в {s.everyNWeeks} нед. </span> : null}
                {!s.active ? <span className="badge amber">на паузе</span> : null}
                <div className="muted">{s.items.map((i) => `${i.product.name} × ${i.qty}`).join(', ')}</div>
              </div>
            ))
          ) : (
            <Empty>Подписки нет — заказы разовые</Empty>
          )}
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
            Пауза не трогает уже созданные заказы — только те, что подписка сделала бы дальше.
          </div>
        </div>

        <div className="card" style={{ flex: '1 1 260px' }}>
          <h2>Каналы связи</h2>
          <div className="muted">
            {c.telegramId ? '✓ Telegram привязан' : '— Telegram не привязан'}
            <br />
            {c.maxId ? '✓ MAX привязан' : '— MAX не привязан'}
          </div>
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
            Привязанный мессенджер — это бесплатные уведомления и вход без SMS.
          </div>
          {c.comment ? <div className="badge amber" style={{ marginTop: 10 }}>{c.comment}</div> : null}
        </div>
      </div>

      <div className="card">
        <div className="tabs">
          <button className={tab === 'orders' ? 'active' : ''} onClick={() => setTab('orders')}>
            Заказы
          </button>
          <button className={tab === 'money' ? 'active' : ''} onClick={() => setTab('money')}>
            Проводки: деньги
          </button>
          <button className={tab === 'bottles' ? 'active' : ''} onClick={() => setTab('bottles')}>
            Проводки: тара
          </button>
        </div>

        {tab === 'orders' ? (
          <table>
            <thead>
              <tr>
                <th>Номер</th>
                <th>Дата</th>
                <th>Состояние</th>
                <th>Оплата</th>
                <th className="num">Сумма</th>
                <th className="num">Тара +/−</th>
              </tr>
            </thead>
            <tbody>
              {c.orders.map((o) => (
                <tr key={o.id}>
                  <td className="mono">
                    <Link to={`/orders/${o.id}`}>{o.number}</Link>
                  </td>
                  <td>{dateFmt(o.deliveryDate)}</td>
                  <td>
                    <Status value={o.status} />
                  </td>
                  <td className="muted">{PAYMENT_RU[o.paymentMethod] ?? o.paymentMethod}</td>
                  <td className="num">{money(o.totalKop)}</td>
                  <td className="num">
                    {o.delivery ? `+${o.delivery.givenFull} / −${o.delivery.takenEmpty}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : tab === 'money' ? (
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Что</th>
                <th>Комментарий</th>
                <th className="num">Сумма</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ledger.data?.money.map((m) => (
                <tr key={m.id}>
                  <td className="muted">{dateTimeFmt(m.createdAt)}</td>
                  <td>{MONEY_KIND_RU[m.kind] ?? m.kind}</td>
                  <td className="muted">
                    {m.comment}
                    {m.order ? <span className="mono"> · {m.order.number}</span> : null}
                  </td>
                  <td className={`num ${BigInt(m.amountKop) < 0n ? 'debt' : 'prepaid'}`}>{money(m.amountKop)}</td>
                  <td>
                    {m.kind !== 'REVERSAL' ? (
                      <button className="ghost" onClick={() => void reverse('money', m.id)}>
                        сторно
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Что</th>
                <th>Комментарий</th>
                <th className="num">Штук</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ledger.data?.bottles.map((x) => (
                <tr key={x.id}>
                  <td className="muted">{dateTimeFmt(x.createdAt)}</td>
                  <td>{BOTTLE_KIND_RU[x.kind] ?? x.kind}</td>
                  <td className="muted">
                    {x.comment}
                    {x.order ? <span className="mono"> · {x.order.number}</span> : null}
                  </td>
                  <td className="num">{x.qty > 0 ? `+${x.qty}` : x.qty}</td>
                  <td>
                    {x.kind !== 'REVERSAL' ? (
                      <button className="ghost" onClick={() => void reverse('bottles', x.id)}>
                        сторно
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
          Проводка не редактируется и не удаляется. Ошибка правится сторно —
          обратной проводкой со ссылкой на исходную.
        </div>
      </div>

      <NewOrder clientId={c.id} addressId={c.addresses[0]?.id} onDone={() => { void reload(); toast.show('Заказ создан'); }} />
      {toast.node}
    </>
  );
}

/**
 * Правка карточки клиента.
 *
 * Здесь нет полей баланса и тары: остаток не редактируется, он сумма проводок
 * (инвариант 2). Меняется то, что действительно свойство клиента — как его
 * зовут, куда звонить, по какой цене возить и сколько разрешено должать.
 */
function EditClient({
  client,
  onClose,
  onSaved,
}: {
  client: Card['client'];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: client.name,
    phone: client.phone,
    comment: client.comment ?? '',
    creditRub: (Number(client.creditLimitKop) / 100).toFixed(2),
    creditBlocked: client.creditBlocked,
    priceRub: client.priceWater19Kop ? (Number(client.priceWater19Kop) / 100).toFixed(2) : '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api(`/clients/${client.id}`, {
            method: 'PATCH',
            body: {
              name: form.name,
              phone: form.phone,
              comment: form.comment || null,
              creditLimitKop: String(Math.round(Number(form.creditRub.replace(',', '.')) * 100) || 0),
              creditBlocked: form.creditBlocked,
              // Пустая персональная цена — значит «по прайсу», а не «ноль рублей»
              priceWater19Kop: form.priceRub.trim()
                ? String(Math.round(Number(form.priceRub.replace(',', '.')) * 100))
                : null,
            },
          });
          onSaved();
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="spread">
        <h2>Правка клиента</h2>
        <button type="button" className="ghost" onClick={onClose}>
          Отмена
        </button>
      </div>
      <div className="row">
        <div className="field" style={{ flex: '2 1 240px' }}>
          <label>Имя или название</label>
          <input name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label>Телефон</label>
          <input name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
        </div>
        <div className="field" style={{ flex: '0 0 160px' }}>
          <label>Своя цена за 19 л, ₽</label>
          <input
            name="priceRub"
            placeholder="по прайсу"
            value={form.priceRub}
            onChange={(e) => setForm({ ...form, priceRub: e.target.value })}
          />
        </div>
        <div className="field" style={{ flex: '0 0 150px' }}>
          <label>Лимит отсрочки, ₽</label>
          <input
            name="creditRub"
            value={form.creditRub}
            onChange={(e) => setForm({ ...form, creditRub: e.target.value })}
          />
        </div>
      </div>
      <div className="row">
        <div className="field" style={{ flex: '1 1 320px' }}>
          <label>Заметка оператора</label>
          <input
            name="comment"
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            placeholder="Звонить после 18:00, код домофона 42"
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-end', margin: '0 0 14px' }}>
          <input
            type="checkbox"
            name="creditBlocked"
            style={{ width: 16 }}
            checked={form.creditBlocked}
            onChange={(e) => setForm({ ...form, creditBlocked: e.target.checked })}
          />
          отсрочка запрещена
        </label>
      </div>
      <ErrorBox error={error} />
      <button className="primary" disabled={busy}>
        Сохранить
      </button>
    </form>
  );
}

function NewAddress({ clientId, onSaved }: { clientId: string; onSaved: () => void }) {
  const [form, setForm] = useState({ street: '', house: '', flat: '', comment: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--n200)' }}
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api(`/clients/${clientId}/addresses`, {
            body: {
              street: form.street,
              house: form.house,
              flat: form.flat || undefined,
              comment: form.comment || undefined,
            },
          });
          onSaved();
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="row">
        <div className="field" style={{ flex: '2 1 160px' }}>
          <label>Улица</label>
          <input name="street" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} required />
        </div>
        <div className="field" style={{ flex: '0 0 80px' }}>
          <label>Дом</label>
          <input name="house" value={form.house} onChange={(e) => setForm({ ...form, house: e.target.value })} required />
        </div>
        <div className="field" style={{ flex: '0 0 80px' }}>
          <label>Кв.</label>
          <input name="flat" value={form.flat} onChange={(e) => setForm({ ...form, flat: e.target.value })} />
        </div>
      </div>
      <div className="field">
        <label>Как проехать</label>
        <input
          name="comment"
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          placeholder="Второй подъезд, домофон 12"
        />
      </div>
      <ErrorBox error={error} />
      <button className="primary" disabled={busy}>
        Добавить адрес
      </button>
    </form>
  );
}

function NewOrder({ clientId, addressId, onDone }: { clientId: string; addressId?: string; onDone: () => void }) {
  const products = useApi<Array<{ id: string; name: string; priceKop: string; kind: string }>>('/products?kind=WATER_19L');
  const [qty, setQty] = useState(2);
  const [date, setDate] = useState(todayIso());
  const [productId, setProductId] = useState('');
  const [method, setMethod] = useState('CASH');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const list = products.data ?? [];
  const chosen = productId || list[0]?.id;

  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!chosen) return;
        setBusy(true);
        setError(null);
        try {
          await api('/orders', {
            body: {
              clientId,
              addressId,
              deliveryDate: date,
              items: [{ productId: chosen, qty }],
              paymentMethod: method,
              channel: 'PHONE',
              idempotencyKey: `op-${clientId}-${date}-${Date.now()}`,
            },
          });
          onDone();
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Новый заказ</h2>
      <div className="row">
        <div className="field" style={{ flex: '2 1 240px' }}>
          <label>Товар</label>
          <select value={chosen} onChange={(e) => setProductId(e.target.value)}>
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {money(p.priceKop)}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '0 0 90px' }}>
          <label>Кол-во</label>
          <input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} />
        </div>
        <div className="field" style={{ flex: '0 0 160px' }}>
          <label>Дата доставки</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field" style={{ flex: '0 0 180px' }}>
          <label>Оплата</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            {Object.entries(PAYMENT_RU).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
          <button className="primary" disabled={busy || !chosen}>
            Создать
          </button>
        </div>
      </div>
      <ErrorBox error={error} />
    </form>
  );
}
